/**
 * DOS518 - Digital Operations System for Wildfire Management
 * Copyright (c) 2024-2025 Angelo Irtilaç — All rights reserved.
 *
 * DiarioOperativo — Log cronologico dell'intervento AIB
 * Registra automaticamente ogni evento significativo.
 * Include gestione Cambio DOS e note manuali del DOS.
 * Esportabile come verbale ufficiale post-intervento.
 */
// src/components/Diario/DiarioOperativo.tsx
import React, { useState, useRef, useEffect } from 'react';
import { useAppStore } from '../../store/useAppStore';
import type { TipoVoceDiario } from '../../store/useAppStore';

// ================================================================
// COLORI PER TIPO VOCE
// ================================================================
const COLORI: Record<TipoVoceDiario, string> = {
  sistema:        '#6366f1',
  coau:           '#f59e0b',
  personale:      '#3b82f6',
  mezzo_terrestre:'#ef4444',
  mezzo_aereo:    '#06b6d4',
  drone:          '#8b5cf6',
  coordinate:     '#22c55e',
  laces:          '#f97316',
  comunicazione:  '#64748b',
  cambio_dos:     '#ec4899',
  nota_manuale:   '#94a3b8',
  astuto:         '#0ea5e9',
  allerta:        '#dc2626',
};

const FILTRI: { label: string; tipo: TipoVoceDiario | 'tutti' }[] = [
  { label: 'Tutti', tipo: 'tutti' },
  { label: '📋 COAU', tipo: 'coau' },
  { label: '👤 Personale', tipo: 'personale' },
  { label: '🚒 Mezzi', tipo: 'mezzo_terrestre' },
  { label: '🚁 Aerei', tipo: 'mezzo_aereo' },
  { label: '🛸 Droni', tipo: 'drone' },
  { label: '📍 Coordinate', tipo: 'coordinate' },
  { label: '🔄 Cambio DOS', tipo: 'cambio_dos' },
  { label: '📝 Note', tipo: 'nota_manuale' },
  { label: '⚠️ Allerte', tipo: 'allerta' },
];

export const DiarioOperativo: React.FC = () => {
  const diario = useAppStore((s) => s.diarioOperativo);
  const dosAttivo = useAppStore((s) => s.dosAttivo);
  const oraAttivazione = useAppStore((s) => s.oraAttivazione);
  const addNotaManuale = useAppStore((s) => s.addNotaManuale);
  const cambioDOS = useAppStore((s) => s.cambioDOS);
  const clearDiario = useAppStore((s) => s.clearDiario);
  const addVoceDiario = useAppStore((s) => s.addVoceDiario);

  const [filtro, setFiltro] = useState<TipoVoceDiario | 'tutti'>('tutti');
  const [nota, setNota] = useState('');
  const [showCambioDOS, setShowCambioDOS] = useState(false);
  const [nuovoDos, setNuovoDos] = useState('');
  const [briefing, setBriefing] = useState('');
  const [showSetDOS, setShowSetDOS] = useState(!dosAttivo);
  const [nomeDosInput, setNomeDosInput] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);

  // Scrolla in fondo quando arriva nuova voce
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [diario.length]);

  const voci = filtro === 'tutti'
    ? diario
    : diario.filter((v) => v.tipo === filtro || (filtro === 'mezzo_terrestre' && v.tipo === 'mezzo_aereo'));

  const handleNota = () => {
    if (!nota.trim()) return;
    addNotaManuale(nota.trim());
    setNota('');
  };

  const handleCambioDOS = () => {
    if (!nuovoDos.trim()) return;
    cambioDOS(nuovoDos.trim(), briefing.trim() || undefined);
    setNuovoDos('');
    setBriefing('');
    setShowCambioDOS(false);
  };

  const handleSetDOS = () => {
    if (!nomeDosInput.trim()) return;
    const ora = new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
    useAppStore.setState({
      dosAttivo: nomeDosInput.trim(),
      oraAttivazione: ora,
    });
    addVoceDiario('sistema', '🟢', `DOS518 attivato — DOS: ${nomeDosInput.trim()}`, { ora });
    setShowSetDOS(false);
    setNomeDosInput('');
  };

  const handleExportTxt = () => {
    const lines = [
      '═══════════════════════════════════════════════════════',
      '         DOS518 — DIARIO OPERATIVO INTERVENTO AIB',
      '═══════════════════════════════════════════════════════',
      `DOS: ${dosAttivo || '—'}`,
      `Attivazione: ${oraAttivazione || '—'}`,
      `Voci registrate: ${diario.length}`,
      `Export: ${new Date().toLocaleString('it-IT')}`,
      '───────────────────────────────────────────────────────',
      '',
      ...diario.map((v) =>
        `[${v.ora}] ${v.icona} ${v.testo}${v.dosAttivo ? `  (${v.dosAttivo})` : ''}`
      ),
      '',
      '═══════════════════════════════════════════════════════',
      '         FINE DIARIO OPERATIVO',
      '═══════════════════════════════════════════════════════',
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `DOS518_Diario_${new Date().toISOString().slice(0, 10)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ padding: '16px', maxWidth: '900px', margin: '0 auto' }}>

      {/* ── Header ── */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: '16px', flexWrap: 'wrap', gap: '10px',
      }}>
        <div>
          <h3 style={{ margin: 0, color: '#f8fafc', fontSize: '1.3em' }}>
            📓 Diario Operativo
          </h3>
          {dosAttivo && (
            <small style={{ color: '#94a3b8' }}>
              DOS: <strong style={{ color: '#60a5fa' }}>{dosAttivo}</strong>
              {oraAttivazione && ` — attivato alle ${oraAttivazione}`}
              {' '}·{' '}
              <span style={{ color: '#64748b' }}>{diario.length} voci</span>
            </small>
          )}
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button
            onClick={() => setShowCambioDOS(true)}
            style={{
              background: '#831843', color: '#fbcfe8', border: '1px solid #ec4899',
              borderRadius: '6px', padding: '6px 12px', cursor: 'pointer',
              fontSize: '0.85rem', fontWeight: 700,
            }}
          >
            🔄 Cambio DOS
          </button>
          <button
            onClick={handleExportTxt}
            disabled={diario.length === 0}
            style={{
              background: '#164e63', color: '#67e8f9', border: '1px solid #0891b2',
              borderRadius: '6px', padding: '6px 12px', cursor: 'pointer',
              fontSize: '0.85rem', fontWeight: 700,
            }}
          >
            ⬇️ Esporta verbale
          </button>
          <button
            onClick={() => { if (confirm('Cancellare tutto il diario?')) clearDiario(); }}
            disabled={diario.length === 0}
            style={{
              background: '#450a0a', color: '#fca5a5', border: '1px solid #dc2626',
              borderRadius: '6px', padding: '6px 12px', cursor: 'pointer',
              fontSize: '0.85rem',
            }}
          >
            🗑️ Reset
          </button>
        </div>
      </div>

      {/* ── Imposta DOS (prima volta) ── */}
      {showSetDOS && (
        <div style={{
          background: '#0f172a', border: '2px solid #6366f1',
          borderRadius: '10px', padding: '16px', marginBottom: '16px',
        }}>
          <p style={{ color: '#a5b4fc', margin: '0 0 10px', fontWeight: 700 }}>
            🟢 Identifica il DOS per il diario operativo
          </p>
          <div style={{ display: 'flex', gap: '8px' }}>
            <input
              type="text"
              placeholder="Nome e Cognome DOS"
              value={nomeDosInput}
              onChange={(e) => setNomeDosInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSetDOS()}
              style={{
                flex: 1, background: '#1e293b', color: '#f1f5f9',
                border: '1px solid #6366f1', borderRadius: '6px',
                padding: '8px 12px', fontSize: '1rem',
              }}
            />
            <button
              onClick={handleSetDOS}
              style={{
                background: '#6366f1', color: '#fff', border: 'none',
                borderRadius: '6px', padding: '8px 16px', cursor: 'pointer',
                fontWeight: 700,
              }}
            >
              Attiva
            </button>
          </div>
        </div>
      )}

      {/* ── Filtri ── */}
      <div style={{
        display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '12px',
      }}>
        {FILTRI.map((f) => (
          <button
            key={f.tipo}
            onClick={() => setFiltro(f.tipo)}
            style={{
              background: filtro === f.tipo ? COLORI[f.tipo as TipoVoceDiario] || '#6366f1' : '#1e293b',
              color: filtro === f.tipo ? '#fff' : '#94a3b8',
              border: `1px solid ${filtro === f.tipo ? 'transparent' : '#334155'}`,
              borderRadius: '20px', padding: '4px 12px',
              cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600,
              transition: 'all 0.15s',
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* ── Timeline voci ── */}
      <div style={{
        background: '#0f172a', border: '1px solid #1e293b',
        borderRadius: '10px', padding: '12px',
        minHeight: '300px', maxHeight: '450px', overflowY: 'auto',
      }}>
        {voci.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#475569', padding: '40px 0' }}>
            {diario.length === 0
              ? '📓 Il diario è vuoto — le azioni in DOS518 verranno registrate automaticamente'
              : '🔍 Nessuna voce per questo filtro'}
          </div>
        ) : (
          voci.map((v, i) => (
            <div
              key={v.id}
              style={{
                display: 'flex', gap: '10px', alignItems: 'flex-start',
                padding: '8px 0',
                borderBottom: i < voci.length - 1 ? '1px solid #1e293b' : 'none',
              }}
            >
              {/* Ora */}
              <span style={{
                color: '#475569', fontSize: '0.8rem', fontFamily: 'monospace',
                minWidth: '42px', paddingTop: '2px',
              }}>
                {v.ora}
              </span>

              {/* Indicatore tipo */}
              <div style={{
                width: '6px', minWidth: '6px', borderRadius: '3px',
                background: COLORI[v.tipo] || '#64748b',
                alignSelf: 'stretch', marginTop: '3px',
              }} />

              {/* Icona */}
              <span style={{ fontSize: '1.1rem', lineHeight: 1.4 }}>{v.icona}</span>

              {/* Testo */}
              <div style={{ flex: 1 }}>
                <span style={{
                  color: v.tipo === 'allerta' ? '#fca5a5'
                    : v.tipo === 'cambio_dos' ? '#f9a8d4'
                    : v.tipo === 'nota_manuale' ? '#cbd5e1'
                    : '#e2e8f0',
                  fontSize: '0.9rem',
                  fontWeight: v.tipo === 'cambio_dos' || v.tipo === 'allerta' ? 700 : 400,
                }}>
                  {v.testo}
                </span>
                {v.dosAttivo && (
                  <span style={{
                    color: '#475569', fontSize: '0.75rem', marginLeft: '8px',
                  }}>
                    {v.dosAttivo}
                  </span>
                )}
              </div>
            </div>
          ))
        )}
        <div ref={bottomRef} />
      </div>

      {/* ── Nota manuale ── */}
      <div style={{
        display: 'flex', gap: '8px', marginTop: '12px',
      }}>
        <input
          type="text"
          placeholder="📝 Aggiungi nota manuale al diario..."
          value={nota}
          onChange={(e) => setNota(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleNota()}
          style={{
            flex: 1, background: '#1e293b', color: '#f1f5f9',
            border: '1px solid #334155', borderRadius: '6px',
            padding: '8px 12px', fontSize: '0.9rem',
          }}
        />
        <button
          onClick={handleNota}
          disabled={!nota.trim()}
          style={{
            background: nota.trim() ? '#1e3a5f' : '#1e293b',
            color: nota.trim() ? '#93c5fd' : '#475569',
            border: `1px solid ${nota.trim() ? '#2563eb' : '#334155'}`,
            borderRadius: '6px', padding: '8px 14px',
            cursor: nota.trim() ? 'pointer' : 'default',
            fontWeight: 700, transition: 'all 0.15s',
          }}
        >
          Aggiungi
        </button>
      </div>

      {/* ── Modal Cambio DOS ── */}
      {showCambioDOS && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 10000,
        }}>
          <div style={{
            background: '#0f172a', border: '2px solid #ec4899',
            borderRadius: '14px', padding: '28px', width: '90%', maxWidth: '480px',
          }}>
            <h3 style={{ color: '#f9a8d4', margin: '0 0 20px' }}>
              🔄 Passaggio Consegna DOS
            </h3>

            <div style={{ marginBottom: '14px' }}>
              <label style={{ color: '#94a3b8', fontSize: '0.85rem' }}>
                DOS uscente
              </label>
              <div style={{
                background: '#1e293b', color: '#64748b',
                border: '1px solid #334155', borderRadius: '6px',
                padding: '8px 12px', marginTop: '4px',
              }}>
                {dosAttivo || '(non impostato)'}
              </div>
            </div>

            <div style={{ marginBottom: '14px' }}>
              <label style={{ color: '#94a3b8', fontSize: '0.85rem' }}>
                DOS entrante *
              </label>
              <input
                type="text"
                placeholder="Nome e Cognome"
                value={nuovoDos}
                onChange={(e) => setNuovoDos(e.target.value)}
                autoFocus
                style={{
                  width: '100%', boxSizing: 'border-box',
                  background: '#1e293b', color: '#f1f5f9',
                  border: '1px solid #ec4899', borderRadius: '6px',
                  padding: '8px 12px', marginTop: '4px', fontSize: '1rem',
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ color: '#94a3b8', fontSize: '0.85rem' }}>
                Note briefing (opzionale)
              </label>
              <textarea
                rows={3}
                placeholder="Situazione operativa, criticità, istruzioni..."
                value={briefing}
                onChange={(e) => setBriefing(e.target.value)}
                style={{
                  width: '100%', boxSizing: 'border-box',
                  background: '#1e293b', color: '#f1f5f9',
                  border: '1px solid #334155', borderRadius: '6px',
                  padding: '8px 12px', marginTop: '4px',
                  fontSize: '0.9rem', resize: 'vertical',
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => { setShowCambioDOS(false); setNuovoDos(''); setBriefing(''); }}
                style={{
                  flex: 1, background: '#1e293b', color: '#94a3b8',
                  border: '1px solid #334155', borderRadius: '8px',
                  padding: '10px', cursor: 'pointer',
                }}
              >
                Annulla
              </button>
              <button
                onClick={handleCambioDOS}
                disabled={!nuovoDos.trim()}
                style={{
                  flex: 2,
                  background: nuovoDos.trim()
                    ? 'linear-gradient(135deg, #831843, #be185d)'
                    : '#334155',
                  color: nuovoDos.trim() ? '#fff' : '#64748b',
                  border: 'none', borderRadius: '8px',
                  padding: '10px', cursor: nuovoDos.trim() ? 'pointer' : 'default',
                  fontWeight: 700, fontSize: '1rem',
                }}
              >
                ✅ Conferma Cambio DOS
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
